// Jurisdiction = a regulatory bucket we map each ISO-2 country to.
// The same wording is reused across UI, IA prompt, PDF and emails so
// that a visitor in FR/BE/US/UK gets the appropriate disclaimer.

import type { Locale } from "@/i18n/types";

export type Jurisdiction =
  | "EU_STRICT"
  | "UK"
  | "CH_NO"
  | "US"
  | "CA"
  | "AU_NZ"
  | "DEFAULT";

const EU_STRICT = new Set([
  "FR", "BE", "DE", "IT", "ES", "NL", "LU", "AT", "PT", "IE",
  "FI", "SE", "DK", "PL", "CZ", "SK", "HU", "RO", "BG", "GR",
  "HR", "SI", "EE", "LV", "LT", "MT", "CY",
]);

export function countryToJurisdiction(country: string | null | undefined): Jurisdiction {
  if (!country) return "DEFAULT";
  const c = country.toUpperCase().slice(0, 2);
  if (EU_STRICT.has(c)) return "EU_STRICT";
  if (c === "GB" || c === "UK") return "UK";
  if (c === "CH" || c === "NO" || c === "IS" || c === "LI") return "CH_NO";
  if (c === "US") return "US";
  if (c === "CA") return "CA";
  if (c === "AU" || c === "NZ") return "AU_NZ";
  return "DEFAULT";
}

// Country list shown in the footer selector. ISO-2 + emoji flag.
export const COUNTRY_CHOICES: { code: string; flag: string; nameFr: string; nameEn: string; nameNl: string }[] = [
  { code: "FR", flag: "🇫🇷", nameFr: "France", nameEn: "France", nameNl: "Frankrijk" },
  { code: "BE", flag: "🇧🇪", nameFr: "Belgique", nameEn: "Belgium", nameNl: "België" },
  { code: "CH", flag: "🇨🇭", nameFr: "Suisse", nameEn: "Switzerland", nameNl: "Zwitserland" },
  { code: "LU", flag: "🇱🇺", nameFr: "Luxembourg", nameEn: "Luxembourg", nameNl: "Luxemburg" },
  { code: "CA", flag: "🇨🇦", nameFr: "Canada", nameEn: "Canada", nameNl: "Canada" },
  { code: "DE", flag: "🇩🇪", nameFr: "Allemagne", nameEn: "Germany", nameNl: "Duitsland" },
  { code: "NL", flag: "🇳🇱", nameFr: "Pays-Bas", nameEn: "Netherlands", nameNl: "Nederland" },
  { code: "IT", flag: "🇮🇹", nameFr: "Italie", nameEn: "Italy", nameNl: "Italië" },
  { code: "ES", flag: "🇪🇸", nameFr: "Espagne", nameEn: "Spain", nameNl: "Spanje" },
  { code: "PT", flag: "🇵🇹", nameFr: "Portugal", nameEn: "Portugal", nameNl: "Portugal" },
  { code: "GB", flag: "🇬🇧", nameFr: "Royaume-Uni", nameEn: "United Kingdom", nameNl: "Verenigd Koninkrijk" },
  { code: "US", flag: "🇺🇸", nameFr: "États-Unis", nameEn: "United States", nameNl: "Verenigde Staten" },
  { code: "AU", flag: "🇦🇺", nameFr: "Australie", nameEn: "Australia", nameNl: "Australië" },
  { code: "NZ", flag: "🇳🇿", nameFr: "Nouvelle-Zélande", nameEn: "New Zealand", nameNl: "Nieuw-Zeeland" },
];

export type DisclaimerPack = {
  /** Short line shown in the footer / PDF footer. */
  footer: string;
  /** Banner above protocols & analyses. */
  banner: string;
  /** Emergency number to point users to. */
  emergency: string;
  /** Wording appended to supplement tips. */
  supplement: string;
  /** Wording appended to nutrition tips. */
  nutrition: string;
  /** Plain-text guidance injected in the AI system prompt. */
  aiGuidance: string;
};

const PACKS: Record<Jurisdiction, Record<Locale, DisclaimerPack>> = {
  EU_STRICT: {
    fr: {
      footer: "Glow Up Coach — Outil de bien-être. Ne constitue pas un avis médical. Données traitées dans l'UE (RGPD).",
      banner: "Cet outil de bien-être ne remplace pas une consultation médicale. En cas de symptôme inhabituel, consultez un professionnel de santé.",
      emergency: "Urgences UE : 112",
      supplement: "Avis non médical. Consultez un médecin ou pharmacien avant toute supplémentation, en particulier en cas de grossesse, traitement en cours ou pathologie.",
      nutrition: "Conseils de bien-être généraux. Pour tout régime spécifique, consultez un diététicien-nutritionniste.",
      aiGuidance: "Réponds en respectant le cadre réglementaire UE : pas d'allégation thérapeutique, pas de diagnostic, pas de promesse de guérison. Pour tout conseil de supplément ou de nutrition spécifique, inclus dans 'disclaimer' une recommandation explicite de consulter un professionnel de santé (médecin, pharmacien, diététicien) et mentionne les éventuelles contre-indications usuelles.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Not medical advice. Data processed in the EU (GDPR).",
      banner: "This wellness tool does not replace a medical consultation. If you notice unusual symptoms, please consult a healthcare professional.",
      emergency: "EU emergency: 112",
      supplement: "Not medical advice. Consult a doctor or pharmacist before any supplementation, especially if pregnant, undergoing treatment or with a known condition.",
      nutrition: "General wellness guidance. For any specific diet, consult a registered dietitian.",
      aiGuidance: "Respect the EU regulatory frame: no therapeutic claim, no diagnosis, no cure promise. For any supplement or specific nutrition tip, include in 'disclaimer' an explicit recommendation to consult a healthcare professional (doctor, pharmacist, dietitian) and mention the usual contra-indications.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Geen medisch advies. Gegevens verwerkt in de EU (AVG).",
      banner: "Deze welzijnstool vervangt geen medisch consult. Bij ongewone symptomen raadpleeg een zorgverlener.",
      emergency: "Noodnummer EU: 112",
      supplement: "Geen medisch advies. Raadpleeg een arts of apotheker vóór elke supplementatie, in het bijzonder bij zwangerschap, behandeling of aandoening.",
      nutrition: "Algemene welzijnstips. Raadpleeg een diëtist voor een specifiek dieet.",
      aiGuidance: "Respecteer het EU-kader: geen therapeutische claims, geen diagnose, geen genezingsbelofte. Voeg bij supplement- en voedingstips altijd een verwijzing naar een zorgverlener toe in 'disclaimer'.",
    },
  },
  UK: {
    fr: {
      footer: "Glow Up Coach — Outil de bien-être. Ne constitue pas un avis médical. Données conformes UK GDPR (ICO).",
      banner: "This wellness tool is not a medical device. In case of unusual symptoms, please contact NHS 111 or your GP.",
      emergency: "UK emergency: 999 — NHS 111 for non-urgent advice",
      supplement: "Not medical advice. Supplements are regulated by the MHRA. Consult a GP or pharmacist before use, particularly during pregnancy, ongoing treatment or chronic conditions.",
      nutrition: "General wellness guidance. For any specific diet, consult a registered dietitian.",
      aiGuidance: "Respect UK rules: no medicinal claims (MHRA), no diagnosis. For supplements and specific nutrition tips, include an explicit recommendation to consult a GP or pharmacist in 'disclaimer'.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Not medical advice. UK GDPR compliant (ICO).",
      banner: "This wellness tool is not a medical device. In case of unusual symptoms, please contact NHS 111 or your GP.",
      emergency: "UK emergency: 999 — NHS 111 for non-urgent advice",
      supplement: "Not medical advice. Supplements are regulated by the MHRA. Consult a GP or pharmacist before use, particularly during pregnancy, ongoing treatment or chronic conditions.",
      nutrition: "General wellness guidance. For any specific diet, consult a registered dietitian.",
      aiGuidance: "Respect UK rules (MHRA, no medicinal claims). For supplements/nutrition include consult-a-GP wording in 'disclaimer'.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Geen medisch advies. UK GDPR conform (ICO).",
      banner: "Deze tool is geen medisch hulpmiddel. Bij ongewone symptomen: bel NHS 111 of je huisarts.",
      emergency: "VK noodnummer: 999 — NHS 111",
      supplement: "Geen medisch advies. Supplementen vallen onder de MHRA. Raadpleeg een arts of apotheker.",
      nutrition: "Algemene welzijnstips. Raadpleeg een diëtist voor een specifiek dieet.",
      aiGuidance: "Respecteer de UK-regels (MHRA, geen geneeskundige claims). Voeg bij supplement-/voedingstips een raadpleeg-de-huisarts-zin toe in 'disclaimer'.",
    },
  },
  CH_NO: {
    fr: {
      footer: "Glow Up Coach — Outil de bien-être. Ne constitue pas un avis médical. Compatible nLPD (Suisse) / GDPR (EEE).",
      banner: "Cet outil de bien-être ne remplace pas une consultation médicale. En cas de doute, consultez un professionnel de santé.",
      emergency: "Urgences : 112 (CH/NO) — Sanitaires : 144 (CH), 113 (NO)",
      supplement: "Avis non médical. Les compléments alimentaires sont surveillés par Swissmedic (CH) / Legemiddelverket (NO). Demandez l'avis d'un médecin ou pharmacien.",
      nutrition: "Conseils généraux de bien-être. Pour un régime spécifique, consultez un nutritionniste.",
      aiGuidance: "Respecte le cadre CH/NO : pas d'allégation thérapeutique, mention Swissmedic / Legemiddelverket pour les suppléments, recommandation médicale explicite dans 'disclaimer'.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Not medical advice. CH FADP / EEA GDPR compliant.",
      banner: "This wellness tool does not replace a medical consultation. When in doubt, contact a healthcare professional.",
      emergency: "Emergency: 112 — Medical: 144 (CH), 113 (NO)",
      supplement: "Not medical advice. Supplements are monitored by Swissmedic (CH) / Legemiddelverket (NO). Consult a doctor or pharmacist before use.",
      nutrition: "General wellness guidance. For any specific diet, consult a nutritionist.",
      aiGuidance: "Respect CH/NO framework: no therapeutic claim, mention Swissmedic / Legemiddelverket for supplements, explicit professional-consult wording in 'disclaimer'.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Geen medisch advies. Conform CH nLPD / EER AVG.",
      banner: "Deze welzijnstool vervangt geen medisch consult.",
      emergency: "Noodnummer: 112 — Medisch: 144 (CH), 113 (NO)",
      supplement: "Geen medisch advies. Supplementen worden gemonitord door Swissmedic / Legemiddelverket. Raadpleeg een arts of apotheker.",
      nutrition: "Algemene welzijnstips. Voor een specifiek dieet: raadpleeg een diëtist.",
      aiGuidance: "Respecteer het CH/NO-kader; vermeld Swissmedic/Legemiddelverket bij supplementen in 'disclaimer'.",
    },
  },
  US: {
    fr: {
      footer: "Glow Up Coach — Wellness tool. Statements have not been evaluated by the FDA. Not intended to diagnose, treat, cure or prevent any disease.",
      banner: "This is a wellness tool, not a medical device. If you notice unusual symptoms, please contact your healthcare provider.",
      emergency: "US emergency: 911 — Poison Control: 1-800-222-1222",
      supplement: "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. Consult your physician before use.",
      nutrition: "General wellness guidance. For any specific diet, consult a registered dietitian (RD).",
      aiGuidance: "Comply with FDA rules: never claim a product 'treats', 'cures' or 'prevents' a disease. For supplements, include the mandatory FDA disclaimer in 'disclaimer': 'These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.' Recommend consulting a physician.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Statements have not been evaluated by the FDA. Not intended to diagnose, treat, cure or prevent any disease.",
      banner: "This is a wellness tool, not a medical device. If you notice unusual symptoms, please contact your healthcare provider.",
      emergency: "US emergency: 911 — Poison Control: 1-800-222-1222",
      supplement: "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. Consult your physician before use.",
      nutrition: "General wellness guidance. For any specific diet, consult a registered dietitian (RD).",
      aiGuidance: "Comply with FDA rules: never claim a product 'treats', 'cures' or 'prevents' a disease. For supplements, include the mandatory FDA disclaimer in 'disclaimer'. Recommend consulting a physician.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Niet beoordeeld door de FDA. Niet bedoeld om ziekten te diagnosticeren, behandelen, genezen of voorkomen.",
      banner: "Dit is een welzijnstool, geen medisch hulpmiddel. Bij ongewone symptomen: raadpleeg een zorgverlener.",
      emergency: "VS noodnummer: 911 — Vergiftiging: 1-800-222-1222",
      supplement: "Deze uitspraken zijn niet beoordeeld door de FDA. Dit product is niet bedoeld om ziekten te diagnosticeren, behandelen, genezen of voorkomen. Raadpleeg uw arts.",
      nutrition: "Algemene welzijnstips. Voor een specifiek dieet: raadpleeg een RD.",
      aiGuidance: "FDA-regels: nooit beweren te behandelen/genezen/voorkomen. Voeg de FDA-disclaimer toe in 'disclaimer'.",
    },
  },
  CA: {
    fr: {
      footer: "Glow Up Coach — Outil de bien-être. Ne constitue pas un avis médical. Conforme LPRPDE.",
      banner: "Outil de bien-être, non un dispositif médical. En cas de symptôme inhabituel, contactez votre médecin.",
      emergency: "Urgences CA : 911 — Info-Santé : 811",
      supplement: "Avis non médical. Les produits de santé naturels sont régulés par Santé Canada. Consultez un médecin ou pharmacien avant tout usage.",
      nutrition: "Conseils généraux. Pour un régime spécifique, consultez un diététiste agréé.",
      aiGuidance: "Respecte Santé Canada : pas d'allégation de traitement/guérison. Mentionne Santé Canada pour les suppléments dans 'disclaimer'.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Not medical advice. PIPEDA compliant.",
      banner: "Wellness tool, not a medical device. If you notice unusual symptoms, contact your physician.",
      emergency: "CA emergency: 911 — Health line: 811",
      supplement: "Not medical advice. Natural health products are regulated by Health Canada. Consult a doctor or pharmacist before use.",
      nutrition: "General guidance. For any specific diet, consult a registered dietitian.",
      aiGuidance: "Respect Health Canada rules: no treatment/cure claims. Mention Health Canada for supplements in 'disclaimer'.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Geen medisch advies. PIPEDA conform.",
      banner: "Welzijnstool, geen medisch hulpmiddel.",
      emergency: "CA noodnummer: 911 — Health line: 811",
      supplement: "Geen medisch advies. Health Canada reguleert natuurproducten. Raadpleeg een arts of apotheker.",
      nutrition: "Algemene welzijnstips. Raadpleeg een diëtist.",
      aiGuidance: "Respecteer Health Canada: geen behandel-/genezingsclaims.",
    },
  },
  AU_NZ: {
    fr: {
      footer: "Glow Up Coach — Outil de bien-être. Ne constitue pas un avis médical. Conforme Privacy Act (AU/NZ).",
      banner: "Outil de bien-être, non un dispositif médical (TGA/Medsafe).",
      emergency: "Urgences : 000 (AU) / 111 (NZ)",
      supplement: "Avis non médical. Les compléments sont régulés par la TGA (AU) / Medsafe (NZ). Consultez un médecin.",
      nutrition: "Conseils généraux. Pour un régime spécifique, consultez un diététicien APD/NZRD.",
      aiGuidance: "Respecte TGA/Medsafe. Mentionne-les dans 'disclaimer' pour les suppléments.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Not medical advice. Privacy Act compliant (AU/NZ).",
      banner: "Wellness tool, not a medical device (TGA/Medsafe).",
      emergency: "Emergency: 000 (AU) / 111 (NZ)",
      supplement: "Not medical advice. Supplements are regulated by TGA (AU) / Medsafe (NZ). Consult a doctor before use.",
      nutrition: "General guidance. For any specific diet, consult an APD/NZRD dietitian.",
      aiGuidance: "Respect TGA/Medsafe. Mention them in 'disclaimer' for supplements.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Geen medisch advies. Privacy Act conform (AU/NZ).",
      banner: "Welzijnstool, geen medisch hulpmiddel (TGA/Medsafe).",
      emergency: "Noodnummer: 000 (AU) / 111 (NZ)",
      supplement: "Geen medisch advies. TGA/Medsafe reguleert supplementen.",
      nutrition: "Algemene welzijnstips. Raadpleeg een APD/NZRD-diëtist.",
      aiGuidance: "Respecteer TGA/Medsafe.",
    },
  },
  DEFAULT: {
    fr: {
      footer: "Glow Up Coach — Outil de bien-être. Ne constitue pas un avis médical.",
      banner: "Cet outil de bien-être ne remplace pas une consultation médicale.",
      emergency: "En cas d'urgence, contactez les services d'urgence locaux.",
      supplement: "Avis non médical. Consultez un professionnel de santé avant toute supplémentation.",
      nutrition: "Conseils généraux de bien-être. Pour un régime spécifique, consultez un nutritionniste.",
      aiGuidance: "Cadre prudent : pas d'allégation thérapeutique, pas de diagnostic. Pour suppléments/nutrition, recommande de consulter un professionnel de santé dans 'disclaimer'.",
    },
    en: {
      footer: "Glow Up Coach — Wellness tool. Not medical advice.",
      banner: "This wellness tool does not replace a medical consultation.",
      emergency: "In case of emergency, call your local emergency services.",
      supplement: "Not medical advice. Consult a healthcare professional before any supplementation.",
      nutrition: "General wellness guidance. For any specific diet, consult a nutritionist.",
      aiGuidance: "Cautious framework: no therapeutic claim, no diagnosis. For supplements/nutrition, recommend consulting a healthcare professional in 'disclaimer'.",
    },
    nl: {
      footer: "Glow Up Coach — Welzijnstool. Geen medisch advies.",
      banner: "Deze welzijnstool vervangt geen medisch consult.",
      emergency: "Bel in geval van nood de lokale hulpdiensten.",
      supplement: "Geen medisch advies. Raadpleeg een zorgverlener vóór elke supplementatie.",
      nutrition: "Algemene welzijnstips. Raadpleeg een diëtist voor een specifiek dieet.",
      aiGuidance: "Voorzichtig kader: geen therapeutische claim, geen diagnose. Verwijs naar een zorgverlener bij supplementen/voeding in 'disclaimer'.",
    },
  },
};

export function getDisclaimerPack(j: Jurisdiction, locale: Locale): DisclaimerPack {
  return (PACKS[j] ?? PACKS.DEFAULT)[locale] ?? PACKS.DEFAULT.en;
}
